import React, { useEffect, useState } from 'react';

const ComingSoonModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-md">
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-cyan-300/20 bg-slate-950 shadow-2xl shadow-cyan-950/40">
        <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-cyan-300/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-emerald-300/10 blur-3xl" />

        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-lg font-black text-slate-200 shadow-lg shadow-slate-950/30 transition hover:-translate-y-0.5 hover:border-rose-300/40 hover:bg-rose-400/10 hover:text-rose-100"
        >
          ×
        </button>

        <div className="relative z-10 flex flex-col items-center px-6 py-9 text-center sm:px-8">
          <div className="mb-7 flex h-40 w-40 items-center justify-center rounded-[2rem] border border-cyan-300/25 bg-cyan-400/10 p-4 shadow-2xl shadow-cyan-950/40 sm:h-52 sm:w-52">
            <img
              src="/BlueLogo.png"
              alt="Subtitle Tsunami"
              className="h-full w-full object-contain"
            />
          </div>

          <p className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">
            Subtitle Editor
          </p>

          <h2 className="mt-3 text-2xl font-black tracking-tight text-white sm:text-3xl">
            Feature Coming Soon
          </h2>

          <p className="mt-4 max-w-md text-sm leading-6 text-slate-300">
            The subtitle editing workspace is being built now. Soon you’ll be able to review, adjust, and clean up subtitles before downloading your final video.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-7 inline-flex w-full items-center justify-center rounded-2xl border border-cyan-300/35 bg-cyan-400/15 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-cyan-100 shadow-lg shadow-cyan-950/30 transition hover:-translate-y-0.5 hover:border-emerald-200/80 hover:bg-emerald-300/15 sm:w-auto"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
};

const VideoPreviewCard = ({
  eyebrow,
  title,
  videoURL,
  videoRef,
  emptyIcon,
  emptyTitle,
  emptyText,
  toggleFullscreen,
  isProcessedCard = false,
  isPreviewAvailable = false,
  handleDownloadVideo,
  countdown,
  formatCountdown,
  user,
  isUploading,
  handleFileChange,
  fileInputResetKey,
  enableEmptyUpload = false,
}) => {
  const [videoError, setVideoError] = useState('');
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  useEffect(() => {
    setVideoError('');

    if (videoRef?.current && videoURL) {
      videoRef.current.load();
    }
  }, [videoURL, videoRef]);

  const handleVideoError = (event) => {
    const mediaError = event.currentTarget.error;

    let message = 'The video URL loaded, but the browser could not play it.';

    if (mediaError) {
      if (mediaError.code === 1) {
        message = 'Video loading was aborted.';
      } else if (mediaError.code === 2) {
        message = 'Network error while loading the video.';
      } else if (mediaError.code === 3) {
        message = 'The video file could not be decoded.';
      } else if (mediaError.code === 4) {
        message = 'The video format or URL is not supported by the browser.';
      }
    }

    console.error(`${title} preview failed:`, {
      videoURL,
      mediaError,
    });

    setVideoError(message);
  };

  const shouldShowProcessedGlow = Boolean(
    isProcessedCard && isPreviewAvailable && videoURL && !videoError
  );

  const shouldShowDownloadButton = Boolean(
    isProcessedCard &&
      isPreviewAvailable &&
      videoURL &&
      !videoError &&
      handleDownloadVideo
  );

  const shouldShowTimer =
    countdown !== null &&
    countdown > 0 &&
    typeof formatCountdown === 'function';

  const canUseEmptyUpload = Boolean(
    enableEmptyUpload &&
      typeof handleFileChange === 'function' &&
      !isUploading
  );

  const emptyStateContent = (
    <div className="relative z-10 flex max-w-sm flex-col items-center justify-center text-center">
      <div
        className={[
          'mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl border text-3xl shadow-lg transition',
          canUseEmptyUpload
            ? 'border-cyan-300/35 bg-cyan-400/15 shadow-cyan-950/30 group-hover:-translate-y-0.5 group-hover:border-emerald-200/70 group-hover:bg-emerald-400/15'
            : 'border-cyan-300/20 bg-cyan-400/10 shadow-cyan-950/30',
        ].join(' ')}
      >
        {emptyIcon}
      </div>

      <p className="text-sm font-black text-white">
        {emptyTitle}
      </p>

      <p className="mt-3 text-sm leading-6 text-slate-400">
        {emptyText}
      </p>

      {enableEmptyUpload && (
        <>
          <span
            className={[
              'mt-5 inline-flex min-h-[42px] items-center justify-center rounded-xl border px-4 py-2 text-xs font-black uppercase tracking-[0.12em] shadow-lg transition',
              user
                ? 'border-emerald-300/35 bg-emerald-400/10 text-emerald-100 shadow-emerald-950/20 group-hover:-translate-y-0.5 group-hover:border-lime-200/80 group-hover:bg-lime-300/15'
                : 'border-amber-300/25 bg-amber-400/10 text-amber-100 shadow-amber-950/20',
            ].join(' ')}
          >
            {user ? 'Choose Video' : 'Sign In To Upload'}
          </span>

          {isUploading && (
            <p className="mt-3 text-xs font-bold text-cyan-100/70">
              Upload in progress...
            </p>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <div
        className={[
          'relative overflow-hidden rounded-3xl bg-slate-950/70 shadow-2xl transition duration-500',
          shouldShowProcessedGlow
            ? 'border border-emerald-300/45 shadow-emerald-950/35'
            : 'border border-white/10 shadow-slate-950/30',
        ].join(' ')}
      >
        {shouldShowProcessedGlow && (
          <>
            <style>
              {`
                @keyframes processedPreviewBorderGlow {
                  0% {
                    opacity: 0.45;
                    transform: translate3d(-35%, -35%, 0) rotate(0deg);
                  }

                  50% {
                    opacity: 0.95;
                    transform: translate3d(15%, 15%, 0) rotate(180deg);
                  }

                  100% {
                    opacity: 0.45;
                    transform: translate3d(-35%, -35%, 0) rotate(360deg);
                  }
                }

                @keyframes processedPreviewPulse {
                  0% {
                    opacity: 0.22;
                    transform: scale(0.98);
                  }

                  50% {
                    opacity: 0.52;
                    transform: scale(1.015);
                  }

                  100% {
                    opacity: 0.22;
                    transform: scale(0.98);
                  }
                }

                @keyframes processedPreviewSheen {
                  0% {
                    opacity: 0;
                    transform: translate3d(-140%, 0, 0) skewX(-18deg);
                  }

                  38% {
                    opacity: 0;
                    transform: translate3d(-95%, 0, 0) skewX(-18deg);
                  }

                  52% {
                    opacity: 0.45;
                    transform: translate3d(15%, 0, 0) skewX(-18deg);
                  }

                  70% {
                    opacity: 0;
                    transform: translate3d(145%, 0, 0) skewX(-18deg);
                  }

                  100% {
                    opacity: 0;
                    transform: translate3d(145%, 0, 0) skewX(-18deg);
                  }
                }
              `}
            </style>

            <div
              className="pointer-events-none absolute -inset-24 z-0 bg-[conic-gradient(from_90deg,transparent,rgba(52,211,153,0.16),rgba(34,211,238,0.2),rgba(163,230,53,0.14),transparent)] blur-2xl will-change-transform"
              style={{
                animation: 'processedPreviewBorderGlow 8s linear infinite',
              }}
            />

            <div
              className="pointer-events-none absolute -inset-1 z-0 rounded-3xl border border-emerald-200/35 opacity-0 will-change-transform"
              style={{
                animation: 'processedPreviewPulse 4.8s ease-in-out infinite',
              }}
            />

            <div className="pointer-events-none absolute inset-0 z-0 rounded-3xl bg-gradient-to-br from-emerald-300/8 via-transparent to-cyan-300/8" />
          </>
        )}

        <div className="relative z-10 border-b border-white/10 px-5 py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p
                className={[
                  'text-xs font-black uppercase tracking-[0.28em]',
                  shouldShowProcessedGlow ? 'text-emerald-300' : 'text-cyan-300',
                ].join(' ')}
              >
                {eyebrow}
              </p>

              <h3 className="mt-2 text-xl font-black text-white">
                {title}
              </h3>
            </div>

            {shouldShowProcessedGlow && (
              <div className="w-fit rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-emerald-200 shadow-lg shadow-emerald-950/25">
                Preview Ready
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10 p-5">
          <div
            className={[
              'relative flex min-h-[300px] items-center justify-center overflow-hidden rounded-2xl bg-slate-950/80 p-4 transition duration-500',
              shouldShowProcessedGlow
                ? 'border border-emerald-300/35 shadow-2xl shadow-emerald-950/30'
                : 'border border-white/10',
              canUseEmptyUpload && !videoURL
                ? 'cursor-pointer hover:border-emerald-300/45 hover:bg-emerald-400/[0.04]'
                : '',
            ].join(' ')}
          >
            {shouldShowProcessedGlow && (
              <>
                <div
                  className="pointer-events-none absolute -inset-8 rounded-3xl bg-emerald-300/10 blur-2xl will-change-transform"
                  style={{
                    animation: 'processedPreviewPulse 4.8s ease-in-out infinite',
                  }}
                />

                <div
                  className="pointer-events-none absolute -left-1/3 top-0 h-full w-1/2 bg-gradient-to-r from-white/0 via-white/18 to-white/0 opacity-0 will-change-transform"
                  style={{
                    animation: 'processedPreviewSheen 5.8s ease-in-out infinite',
                  }}
                />
              </>
            )}

            {videoURL ? (
              <div className="relative z-10 w-full">
                <video
                  key={videoURL}
                  ref={videoRef}
                  controls
                  playsInline
                  preload="metadata"
                  className={[
                    'max-h-[420px] w-full rounded-xl bg-black transition duration-500',
                    shouldShowProcessedGlow
                      ? 'ring-1 ring-emerald-300/35 shadow-2xl shadow-emerald-950/40'
                      : '',
                  ].join(' ')}
                  onDoubleClick={() => toggleFullscreen(videoRef)}
                  onError={handleVideoError}
                >
                  <source src={videoURL} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>

                {videoError && (
                  <div className="mt-4 rounded-2xl border border-rose-300/20 bg-rose-400/10 px-4 py-3">
                    <p className="text-sm font-bold text-rose-100">
                      Preview could not load.
                    </p>

                    <p className="mt-1 text-xs leading-5 text-rose-100/70">
                      {videoError}
                    </p>
                  </div>
                )}
              </div>
            ) : canUseEmptyUpload ? (
              <label className="group absolute inset-0 z-20 flex cursor-pointer items-center justify-center p-4">
                {emptyStateContent}

                <input
                  key={`preview-empty-upload-${fileInputResetKey}`}
                  type="file"
                  accept="video/*"
                  disabled={!user || isUploading}
                  onChange={handleFileChange}
                  className="sr-only"
                />
              </label>
            ) : (
              emptyStateContent
            )}
          </div>

          {shouldShowDownloadButton && (
            <div className="mt-4 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 p-4 shadow-lg shadow-emerald-950/20">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-black text-emerald-200">
                  Your processed video is ready.
                </p>

                {shouldShowTimer && (
                  <p className="text-xs font-bold text-emerald-100/70">
                    Expires in {formatCountdown()}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleDownloadVideo}
                className="mt-4 inline-flex w-full items-center justify-center rounded-2xl border border-emerald-300/35 bg-emerald-400/15 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-emerald-100 shadow-lg shadow-emerald-950/30 transition hover:-translate-y-0.5 hover:border-lime-200/80 hover:bg-lime-300/15"
              >
                Download
                {shouldShowTimer && (
                  <span className="ml-2 text-emerald-200/80">
                    ({formatCountdown()})
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setShowComingSoonModal(true)}
                className="mt-3 inline-flex w-full items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-300 shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:border-cyan-300/35 hover:bg-cyan-400/10 hover:text-cyan-100"
              >
                Edit Subtitles
              </button>

              <p className="mt-3 text-xs leading-5 text-slate-400">
                Downloads are temporary and may expire shortly after processing.
                Save your finished video when it is ready.
              </p>
            </div>
          )}
        </div>
      </div>

      {showComingSoonModal && (
        <ComingSoonModal onClose={() => setShowComingSoonModal(false)} />
      )}
    </>
  );
};

const VideoPreviewGrid = ({
  user,
  isUploading,
  handleFileChange,
  fileInputResetKey,
  originalVideoURL,
  processedVideoURL,
  subtitlesURL,
  originalVideoRef,
  processedVideoRef,
  toggleFullscreen,
  handleDownloadVideo,
  countdown,
  formatCountdown,
}) => {
  const finalProcessedVideoURL = processedVideoURL || subtitlesURL;
  const isProcessedPreviewAvailable = Boolean(finalProcessedVideoURL);

  useEffect(() => {
    console.log('VideoPreviewGrid URLs:', {
      originalVideoURL,
      processedVideoURL,
      subtitlesURL,
      finalProcessedVideoURL,
    });
  }, [
    originalVideoURL,
    processedVideoURL,
    subtitlesURL,
    finalProcessedVideoURL,
  ]);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <VideoPreviewCard
        eyebrow="Original Upload"
        title="Source Video"
        videoURL={originalVideoURL}
        videoRef={originalVideoRef}
        toggleFullscreen={toggleFullscreen}
        emptyIcon="🎬"
        emptyTitle="No source video selected"
        emptyText="Click this box or choose a video file above to preview your original upload here."
        user={user}
        isUploading={isUploading}
        handleFileChange={handleFileChange}
        fileInputResetKey={fileInputResetKey}
        enableEmptyUpload
      />

      <VideoPreviewCard
        eyebrow="Subtitle Output"
        title="Processed Video"
        videoURL={finalProcessedVideoURL}
        videoRef={processedVideoRef}
        toggleFullscreen={toggleFullscreen}
        emptyIcon="🌊"
        emptyTitle="Waiting for processed video"
        emptyText="After processing finishes, the translated subtitle version will appear here."
        isProcessedCard
        isPreviewAvailable={isProcessedPreviewAvailable}
        handleDownloadVideo={handleDownloadVideo}
        countdown={countdown}
        formatCountdown={formatCountdown}
      />
    </div>
  );
};

export default VideoPreviewGrid;